import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getCashSession, openCashSession, closeCashSession, getCashSummary, withdrawCash, type CashSession } from '../../../services/CashService';
import { api } from '../../../utils/api';
import { updateOrderStatus } from '../../../services/OrderService';
import { createPayment } from '../../../services/PaymentService';
import { buildReceiptData, type ReceiptData } from '../../../utils/receipt';
import { OfflineQueueService } from '../../../services/OfflineQueueService';
import type { Order } from '../../../types/@types/order';
import type { Table } from '../../../types/@types/table';
import type { PaymentDetail } from '../../../components/CashRegister/PaymentModal';

interface UsePosTransactionsProps {
  isTakeawayMode: boolean;
  activeOrder: Order | null;
  activeOrderTotal: number;
  activeOrderItems: any[];
  activeOrderTotals: any;
  selected: Table | null;
  tables: Table[] | undefined;
  user: any;
  orderCustomer: { customerName: string; customerPhone: string };

  cancelOrderMutation: any;
  unjoinTableMutation: any;
  updateTableMutation: any;

  setCancelModalOpen: (v: boolean) => void;
  setActiveOrderId: (id: string | number | null) => void;
  setCart: (cart: any) => void;
  refetchActiveOrder: () => void;
  refetchTables: () => void;
  setSelected: (table: Table | null) => void;
  sortedTableOrders?: Order[];
  refetchOrdersByTable?: () => void;
}

export function usePosTransactions(props: UsePosTransactionsProps) {
  const [cashSession, setCashSession] = useState<CashSession | null>(null);
  const [showOpenCash, setShowOpenCash] = useState(false);
  const [showCloseCash, setShowCloseCash] = useState(false);
  const [showWithdrawCash, setShowWithdrawCash] = useState(false);
  const [withdrawingCash, setWithdrawingCash] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [receiptToPrint, setReceiptToPrint] = useState<ReceiptData | null>(null);
  const [lastReceipt, setLastReceipt] = useState<ReceiptData | null>(null);
  const [openingCash, setOpeningCash] = useState(false);
  const [closingCash, setClosingCash] = useState(false);
  const [closeCashSummary, setCloseCashSummary] = useState<any>(null);
  const [triggerPaymentAfterOpen, setTriggerPaymentAfterOpen] = useState(false);

  const fetchAndSetSession = useCallback(async () => {
    try {
      const sess = await getCashSession();
      setCashSession(sess);
      return sess;
    } catch (err) {
      return null;
    }
  }, []);

  useEffect(() => {
    fetchAndSetSession().then((sess) => {
      if (!sess) {
        toast.warn('⚠️ La caja está cerrada. Recuerda abrirla para registrar correctamente los movimientos de caja y cobros.', {
          autoClose: 8000,
        });
      }
    });

    const handleUpdated = () => {
      fetchAndSetSession();
    };

    window.addEventListener('cash:updated', handleUpdated);
    return () => {
      window.removeEventListener('cash:updated', handleUpdated);
    };
  }, [fetchAndSetSession]);

  const handlePayClick = async () => {
    if (props.isTakeawayMode) return;
    // Permit charging any active order regardless of its production status, as long as it has a positive total
    if (!props.activeOrder) return;
    const isAlreadyPaid = props.activeOrder.payment_status === 'PAID' || props.activeOrder.paid === true;
    const isCancelled = props.activeOrder.status?.toUpperCase() === 'CANCELLED';
    if (isAlreadyPaid || isCancelled || props.activeOrderTotal <= 0) return;
    try {
      const sess = await getCashSession();
      if (!sess) {
        setTriggerPaymentAfterOpen(true);
        setShowOpenCash(true);
      } else {
        setCashSession(sess);
        setShowPayment(true);
      }
    } catch (err: any) {
      if (err?.isOffline) {
        toast.warn('Sin conexión. Abre la caja cuando vuelva la red.');
      } else {
        toast.error('No se pudo obtener la sesión de caja');
      }
    }
  };

  const handleCancelActiveOrder = (_reason?: string) => {
    if (props.isTakeawayMode || !props.activeOrder?.id) return;
    props.cancelOrderMutation.mutate(
      { orderId: props.activeOrder.id, tableId: props.selected?.id },
      {
        onSuccess: () => {
          props.setCancelModalOpen(false);
          props.setActiveOrderId(null);
          props.setCart({});
        },
      },
    );
  };

  const handleOpenSessionConfirm = async (amount: number) => {
    if (props.isTakeawayMode || !props.user) return;
    setOpeningCash(true);
    try {
      const sess = await openCashSession({ user: props.user.id, initialAmount: amount });
      setCashSession(sess);
      setShowOpenCash(false);
      window.dispatchEvent(new Event('cash:updated'));
      if (triggerPaymentAfterOpen) {
        setShowPayment(true);
        setTriggerPaymentAfterOpen(false);
      }
    } catch (err: any) {
      console.error('[handleOpenSessionConfirm] Error al abrir caja:', err);
      const msg =
        err.response?.data?.message ||
        (err.message === 'Network Error'
          ? 'Error de conexión con el servidor local. Verifica que los servicios estén iniciados.'
          : err.message || 'No se pudo abrir la caja');
      toast.error(msg);
    } finally {
      setOpeningCash(false);
    }
  };

  const handleCloseSessionClick = async () => {
    console.log('[handleCloseSessionClick] cashSession:', cashSession);
    const sid = cashSession?.id || (cashSession as any)?._id || (cashSession as any)?.uuid;
    if (!sid) {
      console.warn('[handleCloseSessionClick] No session id found in cashSession:', cashSession);
      toast.warn('No se detectó un ID de sesión de caja activo.');
      return;
    }
    try {
      const summary = await getCashSummary(String(sid));
      setCloseCashSummary(summary);
      setShowCloseCash(true);
    } catch (err: any) {
      console.error('[handleCloseSessionClick] Error getting summary:', err);
      toast.error('No se pudo obtener el resumen de caja');
    }
  };

  const handleCloseSessionConfirm = async (
    finalAmount: number,
    obs: string,
    cashCount: Record<string, number>,
    paymentBreakdown: any[]
  ) => {
    if (!cashSession?.id) return;
    setClosingCash(true);
    try {
      await closeCashSession({
        sessionId: cashSession.id,
        finalAmount,
        observations: obs,
        cashCount,
        paymentBreakdown,
      });
      setCashSession(null);
      setShowCloseCash(false);
      toast.success('Caja cerrada correctamente');
      window.dispatchEvent(new Event('cash:updated'));
    } catch (err: any) {
      const msg = err.response?.data?.message ?? 'No se pudo cerrar la caja';
      toast.error(msg);
    } finally {
      setClosingCash(false);
    }
  };

  const handleConfirmPayment = async (payments: PaymentDetail[], tipAmount: number = 0) => {
    if (props.isTakeawayMode || !cashSession || !props.activeOrder || !props.selected) return;

    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    if (totalPaid <= 0) {
      toast.warn('Monto insuficiente');
      return;
    }

    const totalReceived = payments.reduce((sum, p) => sum + p.received, 0);
    const change = Math.max(0, totalReceived - totalPaid);
    const paymentTimestamp = new Date().toISOString();
    let receiptPayload: ReceiptData | null = null;

    setProcessingPayment(true);

    const isOffline = !navigator.onLine;

    // ── OFFLINE PATH ───────────────────────────────────────────────────────────
    // Queue payments and status update; generate receipt locally so the cashier
    // can print/see the ticket even without connectivity.
    if (isOffline) {
      try {
        for (const p of payments) {
          await OfflineQueueService.pushOperation('CREATE_PAYMENT', {
            orderId: props.activeOrder.id,
            tableId: props.selected.id,
            amount: p.amount,
            method: p.method,
            cashRegisterId: cashSession.id,
            ...(p.received > p.amount ? { received: p.received, change: p.received - p.amount } : {}),
          });
        }
        await OfflineQueueService.pushOperation('UPDATE_ORDER_STATUS', {
          id: props.activeOrder.id,
          status: 'PAID',
        });

        const subtotal = props.activeOrderTotals.subtotal || totalPaid;
        const discountAmount = Math.max(0, subtotal - totalPaid + (tipAmount || 0));

        receiptPayload = buildReceiptData({
          order: props.activeOrder,
          items: props.activeOrderItems.map((item) => ({
            name: item.name || item.product?.name || 'Producto',
            quantity: item.quantity ?? 0,
            unitPrice: item.unitPrice ?? item.base_price ?? 0,
            total: item.line_subtotal ?? (item.unitPrice ?? item.base_price ?? 0) * (item.quantity ?? 0),
            notes: item.kitchenSummary || item.notes,
          })),
          totals: { subtotal, total: totalPaid },
          discountAmount: discountAmount > 0 ? discountAmount : undefined,
          table: props.selected,
          customerName: props.orderCustomer.customerName,
          payments: payments.map((p) => ({ method: p.method, amount: p.amount, received: p.received })),
          received: totalReceived,
          change,
          paidAt: paymentTimestamp,
        });

        const remainingOrders = (props.sortedTableOrders || []).filter(
          (o) => String(o.id) !== String(props.activeOrder?.id)
        );

        props.setCart({});
        if (remainingOrders.length > 0) {
          props.setActiveOrderId(remainingOrders[0].id);
          localStorage.setItem(`pos:activeOrder:${props.selected.id}`, String(remainingOrders[0].id));
          props.refetchTables();
          props.refetchActiveOrder();
          props.refetchOrdersByTable?.();
        } else {
          localStorage.removeItem(`pos:activeOrder:${props.selected.id}`);
          props.setActiveOrderId(null);
          props.refetchTables();
          props.setSelected(null);
        }
        toast.warn(`Venta guardada OFFLINE — Cambio: $${change.toLocaleString()}. Se sincronizará al recuperar internet.`);
      } catch (e) {
        toast.error('Error al guardar la venta offline');
      } finally {
        setProcessingPayment(false);
        setShowPayment(false);
        if (receiptPayload) { setReceiptToPrint(receiptPayload); setLastReceipt(receiptPayload); }
      }
      return;
    }

    // ── ONLINE PATH ────────────────────────────────────────────────────────────
    try {
      await createPayment({
        orderId: props.activeOrder!.id,
        tableId: props.selected!.id,
        cashRegisterId: cashSession.id,
        payments: payments.map((p) => ({
          method: p.method,
          amount: p.amount,
          received: p.received,
          change: p.received > p.amount ? p.received - p.amount : 0,
        })),
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'No se pudo procesar el pago';
      toast.error(msg);
      setProcessingPayment(false);
      return;
    }

    // Backend (PaymentController) ya marca automáticamente la orden como PAID,
    // descuenta inventario y libera la mesa en la misma transacción atómica de pago.

    // Backend ya se encarga de liberar la mesa y las mesas unidas si no hay más pedidos activos
    // (a través de OrderService::checkAndFreeTable y el evento TableUpdated)

    const subtotal = props.activeOrderTotals.subtotal || totalPaid;
    const discountAmount = Math.max(0, subtotal - totalPaid + (tipAmount || 0));

    receiptPayload = buildReceiptData({
      order: props.activeOrder,
      items: props.activeOrderItems.map((item) => ({
        name: item.name || item.product?.name || 'Producto',
        quantity: item.quantity ?? 0,
        unitPrice: Number(item.unitPrice ?? item.base_price ?? 0),
        total: Number(item.line_subtotal ?? (item.unitPrice ?? item.base_price ?? 0) * (item.quantity ?? 0)),
        notes: item.kitchenSummary || item.notes,
      })),
      totals: { subtotal, total: totalPaid },
      discountAmount: discountAmount > 0 ? discountAmount : undefined,
      table: props.selected,
      customerName: props.orderCustomer.customerName,
      payments: payments.map((p) => ({ method: p.method, amount: p.amount, received: p.received })),
      received: totalReceived,
      change,
      paidAt: paymentTimestamp,
    });

    const remainingOrders = (props.sortedTableOrders || []).filter(
      (o) => String(o.id) !== String(props.activeOrder?.id)
    );

    props.setCart({});
    toast.success(`Venta registrada - Cambio: $${change.toLocaleString()}`);

    if (remainingOrders.length > 0) {
      // Quedan otras cuentas por cobrar en esta mesa: permanecer en la mesa y cambiar a la siguiente cuenta
      props.setActiveOrderId(remainingOrders[0].id);
      localStorage.setItem(`pos:activeOrder:${props.selected.id}`, String(remainingOrders[0].id));
      props.refetchTables();
      props.refetchActiveOrder();
      props.refetchOrdersByTable?.();
    } else {
      // Era la última cuenta pendiente de la mesa: desocupar la selección y volver al mapa de mesas
      localStorage.removeItem(`pos:activeOrder:${props.selected.id}`);
      props.setActiveOrderId(null);
      props.refetchTables();
      props.setSelected(null);
    }
    setProcessingPayment(false);
    setShowPayment(false);
      if (receiptPayload) {
        setReceiptToPrint(receiptPayload);
        setLastReceipt(receiptPayload);
      }
  };

  const handleWithdrawCashClick = async () => {
    let sess = cashSession;
    if (!sess) {
      sess = await fetchAndSetSession();
    }
    if (!sess) {
      toast.warn('⚠️ La caja está cerrada. Debes abrir la caja primero para poder registrar un retiro.', {
        autoClose: 5000,
      });
      setShowOpenCash(true);
      return;
    }
    setShowWithdrawCash(true);
  };

  const handleWithdrawCashConfirm = async (
    amount: number,
    reason: string,
    kind?: string,
    reference?: string,
    openDrawer?: boolean
  ) => {
    let sess = cashSession;
    if (!sess) {
      sess = await fetchAndSetSession();
    }
    if (!sess) {
      toast.error('No hay sesión de caja abierta');
      return;
    }

    setWithdrawingCash(true);
    try {
      await withdrawCash({
        session: sess.id,
        amount,
        reason,
        kind,
        reference,
      });

      if (openDrawer) {
        api.post('/print-jobs/enqueue', { type: 'drawer' }).catch((e) => {
          console.warn('[handleWithdrawCashConfirm] Error al solicitar apertura de cajón:', e);
        });
      }

      toast.success(`Retiro por $${amount.toLocaleString('es-CO')} registrado con éxito`);
      setShowWithdrawCash(false);
      window.dispatchEvent(new Event('cash:updated'));
    } catch (err: any) {
      console.error('[handleWithdrawCashConfirm] Error:', err);
      const msg = err?.response?.data?.message ?? 'No se pudo registrar el retiro de caja';
      toast.error(msg);
    } finally {
      setWithdrawingCash(false);
    }
  };

  return {
    cashSession,
    showOpenCash,
    setShowOpenCash,
    showWithdrawCash,
    setShowWithdrawCash,
    withdrawingCash,
    handleWithdrawCashClick,
    handleWithdrawCashConfirm,
    showPayment,
    setShowPayment,
    processingPayment,
    receiptToPrint,
    setReceiptToPrint,
    lastReceipt,
    openingCash,
    showCloseCash,
    setShowCloseCash,
    closingCash,
    closeCashSummary,
    handleCloseSessionClick,
    handleCloseSessionConfirm,
    handlePayClick,
    handleCancelActiveOrder,
    handleOpenSessionConfirm,
    handleConfirmPayment,
  };
}
